import { ReactNode } from 'react';

export interface TabItem<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
  count?: number;
  updatedAt?: number;
}

interface UnderlineTabsProps<T extends string> {
  tabs: TabItem<T>[];
  activeTab: T;
  onTabChange: (tab: T) => void;
}

export function UnderlineTabs<T extends string>({
  tabs,
  activeTab,
  onTabChange,
}: UnderlineTabsProps<T>) {
  return (
    <div role="tablist" className="flex mb-4 border-b border-border">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={activeTab === tab.value}
          // No border-none here: it zeroes border-style and the underline never paints.
          className={`-mb-px px-5 py-3 bg-transparent border-b-2 text-sm font-medium flex items-center gap-2 transition-colors cursor-pointer outline-none focus-ring rounded-t-md ${
            activeTab === tab.value
              ? 'text-foreground border-b-primary'
              : 'text-muted-foreground border-b-transparent hover:text-foreground'
          }`}
          onClick={() => onTabChange(tab.value)}
        >
          {tab.icon}
          <span key={tab.updatedAt ?? 0} className={tab.updatedAt ? 'baxter-changed rounded-sm' : undefined}>{tab.label}</span>
          {tab.updatedAt && <><span aria-hidden className="size-1.5 shrink-0 rounded-full bg-primary" /><span className="sr-only">Updated</span></>}
          {tab.count !== undefined && tab.count > 0 && (
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums ${
                activeTab === tab.value
                  ? 'bg-muted text-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
